"use client"

import * as React from "react"
import { KeyMetrics } from "@/components/key-metrics"
import { TermResponseTrendChart } from "@/components/evaluation-dashboard/closed-term-response-trend"
import { OtherTermsSection } from "./all-terms-section"
import { OperationsLastClosedTermSection } from "./operations/operations-last-closed-term-section"
import { OperationsTermSection } from "./operations/operations-term-section"
import { EVAL_DASHBOARD_KPI_STRIP_CLASS } from "@/lib/evaluation-dashboard-a11y"
import {
  buildOperationsTermSections,
  evaluationOperationsV3KpiMetrics,
} from "@/lib/mock/evaluation-operations"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface EvaluationDashboardOperationsV3ViewProps {
  currentTerm?: EvaluationTerm
  pastTerms: EvaluationTerm[]
  upcomingTerms: EvaluationTerm[]
  futureTerms: EvaluationTerm[]
}

export function EvaluationDashboardOperationsV3View({
  currentTerm,
  pastTerms,
  upcomingTerms,
  futureTerms,
}: EvaluationDashboardOperationsV3ViewProps) {
  const sections = React.useMemo(() => buildOperationsTermSections(), [])
  const metrics = React.useMemo(() => evaluationOperationsV3KpiMetrics(), [])

  const currentSection = sections.find(section => section.term.timelinePosition === "current")
  const lastSection = sections.find(section => section.term.timelinePosition === "last")
  const otherSections = sections.filter(
    section =>
      section.term.timelinePosition !== "current" &&
      section.term.timelinePosition !== "last",
  )

  const lastTermFromSection = lastSection?.term

  if (sections.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
        No active survey windows across your terms.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-10">
      <KeyMetrics
        variant="cards"
        size="md"
        metrics={metrics}
        showHeader={false}
        metricsSingleRow
        className={EVAL_DASHBOARD_KPI_STRIP_CLASS}
      />

      {currentSection || lastSection ? (
        <div id="eval-operations-terms" className="flex flex-col gap-8">
          <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2">
            {currentSection ? (
              <OperationsTermSection
                term={currentSection.term}
                rows={currentSection.rows}
                showLifecycle={
                  currentTerm != null && currentSection.term.id === currentTerm.id
                }
                layout="paired"
              />
            ) : null}
            {lastSection ? (
              <OperationsLastClosedTermSection
                term={lastSection.term}
                includeTrendChart={false}
                layout="paired"
              />
            ) : null}
          </div>

          {lastTermFromSection ? (
            <TermResponseTrendChart focusTerm={lastTermFromSection} />
          ) : null}
        </div>
      ) : null}

      {otherSections.length > 0 ? (
        <div className="flex flex-col gap-8">
          {otherSections.map(section => (
            <OperationsTermSection
              key={section.term.id}
              term={section.term}
              rows={section.rows}
              showLifecycle={false}
            />
          ))}
        </div>
      ) : null}

      <OtherTermsSection
        pastTerms={pastTerms}
        upcomingTerms={upcomingTerms}
        futureTerms={futureTerms}
      />
    </div>
  )
}
